import { INDICATOR_PRESETS } from "../../Static/Config/indicatorPresets";

/**
 * Field status indicators - the pure resolution layer.
 *
 * A "rule set" describes one indicator: which field on a record carries its value,
 * which field carries the reason, and the list of rules that map a value to a glyph.
 * Rule sets live in code, in INDICATOR_PRESETS. Org meta carries exactly one key -
 * the on/off flag below - so enabling the feature is a one-line config change and
 * every colour, glyph and label stays in the frontend where it is reviewed and tested.
 *
 * Everything here is pure so it can be unit tested without rendering; the component
 * and the hook are thin wrappers over these functions.
 */

export const FIELD_INDICATORS_ENABLED_KEY = "fieldIndicators.enabled";

/**
 * Collapses a status value to a comparable key.
 *
 * The list API and the entity-tags API speak different vocabularies for the same
 * state: the list carries the supported value's displayName ("Non-Operational",
 * "onboarding in progress") while the Digital Twin carries its valueCode
 * ("non_operational", "onboarding_inprogress"). Stripping every non-alphanumeric
 * character collapses both onto one key, so a single rule serves both surfaces.
 *
 * Note this is deliberately NOT normalizeKey() from common_helpers: that converts
 * spaces to underscores but strips hyphens outright, so "Non-Operational" and
 * "non_operational" do not meet there.
 */
export const normalizeIndicatorValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
};

/** True only when the org has explicitly switched the feature on. */
export const isFieldIndicatorsEnabled = (metaInfo) =>
    metaInfo?.globalConfig?.[FIELD_INDICATORS_ENABLED_KEY] === true;

/** Resolves a rule set by name. */
export const getIndicatorConfig = (name) => {
    if (!name) return null;
    return INDICATOR_PRESETS[name] || null;
};

/**
 * Normalises whatever a column config put in `indicators` into an array of names.
 * Accepts a bare string, an array of strings, or an array of objects carrying a
 * `name`/`id`, so older meta shapes keep working.
 */
export const getIndicatorNames = (indicators) => {
    if (!indicators) return [];
    const list = Array.isArray(indicators) ? indicators : [indicators];
    return list
        .map((entry) => (typeof entry === "string" ? entry : entry?.name || entry?.id))
        .filter(Boolean);
};

/**
 * Matches a value against a rule set and builds the tooltip.
 *
 * Returns null when nothing matches - Operational, unset and unrecognised values all
 * land here, and all three must render no marker rather than a fallback one.
 */
export const resolveIndicator = ({ indicator, value, reason }) => {
    const rules = indicator?.rules;
    if (!Array.isArray(rules) || rules.length === 0) return null;

    const normalized = normalizeIndicatorValue(value);
    if (!normalized) return null;

    const matched = rules.find((rule) =>
        (rule?.match || []).some((candidate) => normalizeIndicatorValue(candidate) === normalized)
    );
    if (!matched) return null;

    // The tooltip label comes from the rule, never from the value: the stored display
    // name for onboarding is lower case ("onboarding in progress"), and echoing it
    // would carry that casing into the tooltip.
    let tooltip = matched.tooltip || "";
    if (matched.appendReason && reason) tooltip = tooltip ? `${tooltip}: ${reason}` : String(reason);

    return {
        icon: matched.icon,
        color: matched.color,
        background: matched.background,
        tooltip,
    };
};

/**
 * Reads the value and reason for an indicator off a table row.
 */
export const getIndicatorValuesFromRecord = (record, indicator) => ({
    value: indicator?.sourceField ? record?.[indicator.sourceField] : undefined,
    reason: indicator?.reasonField ? record?.[indicator.reasonField] : undefined,
});

/**
 * Every field the named indicators read from a row.
 *
 * The list endpoint returns only the columns the request names, so a marker whose
 * source column is not currently visible would silently have nothing to read - which
 * is the common case, since users filter on operational status while showing other
 * columns. The list adds these keys to its data request, and only to the request:
 * never to the column picker or the export.
 *
 * Returns [] when the feature is off, so a disabled org's request is unchanged.
 */
export const getIndicatorSourceFields = (names, metaInfo) => {
    if (!isFieldIndicatorsEnabled(metaInfo)) return [];

    const fields = new Set();
    getIndicatorNames(names).forEach((name) => {
        const indicator = getIndicatorConfig(name);
        if (indicator?.sourceField) fields.add(indicator.sourceField);
        if (indicator?.reasonField) fields.add(indicator.reasonField);
    });

    return Array.from(fields);
};

/**
 * Merges the indicator source fields into a requested-columns list, skipping any the
 * user already has visible so the payload carries no duplicates.
 */
export const withIndicatorSourceFields = (requestedColumns, names, metaInfo) => {
    const extras = getIndicatorSourceFields(names, metaInfo)
        .filter((field) => !(requestedColumns || []).includes(field));

    if (!extras.length) return requestedColumns;
    return [...(requestedColumns || []), ...extras];
};
